import * as _ from 'underscore';

import { NaturalLanguageDatasourceOwnerPanelProps } from '@tableau/natural-language-datasource-owner-panel';

import {
  IAllowedActionScope,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  RefreshContentScope,
} from '../../app/ActionPlaceScopes';
import { contentCountsUpToDate } from '../../app/actions/spaces/ContentCountsActions';
import { enterSpace } from '../../app/actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../app/actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../app/actions/spaces/RequestSpaceContentsActions';
import { updateBreadcrumbs } from '../../app/actions/UpdateBreadcrumbsAction';
import { appStore } from '../../app/AppStore';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs,
  watchUrlForBreadcrumbUpdates
} from '../../app/content/breadcrumbs/BreadcrumbHelpers';
import { IPlaceScope } from '../../app/IPlaceScope';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { DatasourceSpaceOwnProps } from '../../app/spaces/DatasourceSpace/DatasourceSpace';
import {
  DetailsDialogInAngularPlaceHelper, IDetailsDialogInAngularPlaceHelper
} from '../../app/spaces/DetailsDialog/AngularContentDetailsDialog';
import { addUrlWatchersToScopeForExplorer } from '../../app/spaces/Explorer/ExplorerUrlBindings';
import { DatasourceSpaceConfigHelper } from '../../app/spaces/SpaceConfig/DatasourceSpaceConfigHelper';
import { clearAndSetSortOrderFromURL } from '../../app/spaces/SpaceConfig/SiteSpaceConfigHelper';
import { SpaceConfigHelper } from '../../app/spaces/SpaceConfig/SpaceConfigHelper';
import { Messages } from '../../codegen/Localize';
import { getQueryStringServiceFromAngular } from '../../common/react/QueryString';
import { AngularContext, AngularContextFactory } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { ActionContext } from '../helpers/ActionContext';
import { RefreshPropertyHelper } from '../helpers/RefreshPropertyHelper';
import { RemoteQueryAgentHelper } from '../helpers/RemoteQueryAgentHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { isDatasourcePlacePath } from '../helpers/UrlHelpers';
import { DatasourceDefinition } from '../models/ContentDefinitions';
import { IDataSourceDetailInfo, IDataSourceDetailInfoWithUsage, IDataSourceInfo } from '../models/IDataSourceInfo';
import { IItemStatisticsInfo, INamedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataSources } from '../resources/Datasources';
import { IGetPreferredRefreshOptionResult } from '../ServerApi/requests/GetPreferredRefreshOption';
import { IDatasourceActions } from '../ServerApi/types/IDatasourceActions';
import { IDatasourceUsageInfo } from '../ServerApi/types/IUsageInfo';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ChangeDatasourceDescriptionAction } from '../services/ChangeDatasourceDescriptionAction';
import { ContentActions } from '../services/ContentActions';
import { DataCatalogApiRequestsService } from '../services/DataCatalogApiRequestsService';
import { DeleteDatasourcesAction, IDeleteDatasourcesActionResult } from '../services/DeleteDatasourcesAction';
import { ModalService } from '../services/ModalService';
import { RevisionHistoryAction } from '../services/RevisionHistoryAction';
import { ServerService } from '../services/ServerService';
import { Telemetry } from '../services/TelemetryService';
import { DatasourcePublishResultService } from './DatasourcePublishResultCtrl';

// Scope and props needed to display the details dialog
export interface IDataSourcePlaceScope extends
  IAllowedActionScope<IDatasourceActions>,
  IChangeDescriptionActionScope,
  IChangeOwnerActionScope,
  IChangeTagsActionScope,
  IDeleteActionScope,
  IPlaceScope,
  RefreshContentScope
{
  datasource: IDataSourceDetailInfo & IItemStatisticsInfo<IDatasourceUsageInfo>;
  datasourceSet: Set<IDataSourceDetailInfo>;
  showRemoteQueryAgent: boolean;
  moveToProject: ($event: JQueryEventObject) => void;
  showRevisionHistory: ($event: JQueryEventObject) => any;
  changeRemoteQueryAgent: ($event: JQueryEventObject) => any;
  showDatasourceCertification: boolean;
  certificationStatusUpdated: ($event: JQueryEventObject) => void;
  currentRemoteQueryAgentDisplayName: () => string;
  datasourceSpaceProps: DatasourceSpaceOwnProps;
  isInOwnerPanel: boolean;
  datasourceOwnerPanelProps: NaturalLanguageDatasourceOwnerPanelProps;

  onNlpSettingSaved: () => void;
}

export class DatasourcePlaceCtrl {

  private currentPath: string;

  public static $inject = [
    '$location',
    '$q',
    '$scope',
    '$sanitize',
    '$state',
    'AngularContextFactory',
    'BrowserTitleService',
    'ChangeDatasourceDescriptionAction',
    'ContentActions',
    'DataCatalogApiRequestsService',
    'DeleteDatasourcesAction',
    'datasourceActions',
    'datasourceDetailInfo',
    'datasourceInfo',
    'projectHierarchy',
    'RevisionHistoryAction',
    'ModalService',
    'TelemetryService',
    'DatasourcePublishResultService',
    'DataSources',
    'ServerService',
  ];

  constructor(
    private $location: ng.ILocationService,
            $q: ng.IQService,
    private $scope: IDataSourcePlaceScope,
            $sanitize: ng.sanitize.ISanitizeService,
    private $state: ng.ui.IStateService,
            angularContextFactory: AngularContextFactory,
            browserTitleService: BrowserTitleService,
    private ChangeDatasourceDescriptionAction: ChangeDatasourceDescriptionAction,
    private ContentActions: ContentActions,
    private dataCatalogApiRequestsService: DataCatalogApiRequestsService,
    private DeleteDatasourcesAction: DeleteDatasourcesAction,
            datasourceActions: IDatasourceActions,
    private datasourceDetailInfo: IDataSourceDetailInfo,
    private datasourceInfo: IDataSourceInfo,
    private projectHierarchy: INamedItemInfo[],
    private RevisionHistoryAction: RevisionHistoryAction,
            modalService: ModalService,
            TelemetryService: Telemetry,
    private datasourcePublishResultService: DatasourcePublishResultService,
    private DataSources: DataSources,
    private ServerService: ServerService,
  ) {
    this.initScopeForBreadcrumbs(this.$scope);
    watchUrlForBreadcrumbUpdates($scope, this.$location,() => isDatasourcePlacePath(this.datasourceDetailInfo.id, this.$location.path()));

    const allowedActionFn = (actionName: keyof IDatasourceActions) => (!!datasourceActions && datasourceActions[actionName]);
    const refreshContentFn = () => $state.hardReload();

    $scope.datasourceOwnerPanelProps = {
      unifiedDatasourceId: datasourceDetailInfo.idForFavoriting,
      datasourceName: datasourceDetailInfo.name,
      siteUrlName: ServerService.sessionSiteUrlName(),
    };

    const datasourceWithUsageInfo = {
      usageInfo: datasourceInfo.usageInfo,
      usageInfoMaxValues: datasourceInfo.usageInfoMaxValues,
      ...datasourceDetailInfo
    };

    const angularContext = angularContextFactory.create($scope);

    const detailsDialogHelper = new DetailsDialogInAngularPlaceHelper(
        refreshContentFn,
        $q,
        angularContext,
        modalService,
        TelemetryService
    );
    this.loadDatasourceIntoScope(datasourceDetailInfo);
    this.initScopeForDetailsPane($scope, $q, allowedActionFn, refreshContentFn, detailsDialogHelper);

    this.constructStylishDatasourceCtrl(
      angularContext,
      $scope,
      datasourceWithUsageInfo,
      $sanitize,
      TelemetryService,
      browserTitleService,
      $location,
      datasourceActions,
      detailsDialogHelper,
      dataCatalogApiRequestsService
    );

    // ensure that we are on the right 'refresh schedules' subspace
    if ($state.includes('**.extractTasks') || ($state.includes('**.remoteRefreshSchedules'))) {
      redirectByExtractsAndRefreshMode(this.$state, this.datasourceDetailInfo);
    }
  }

  private loadDatasourceIntoScope(newDatasourceDetailInfo: IDataSourceDetailInfo) {
    this.datasourceDetailInfo = newDatasourceDetailInfo;

    const datasourceWithUsageInfo = {
      usageInfo: this.datasourceInfo.usageInfo,
      usageInfoMaxValues: this.datasourceInfo.usageInfoMaxValues,
      ...newDatasourceDetailInfo
    };

    this.$scope.datasource = datasourceWithUsageInfo;
    this.$scope.datasourceSet = Set.singletonForIdItem(newDatasourceDetailInfo);

    this.$scope.objectName = newDatasourceDetailInfo.name;
    this.$scope.ownerId = newDatasourceDetailInfo.owner.id;
  }

  private initScopeForDetailsPane(
      $scope: IDataSourcePlaceScope,
      $q: ng.IQService,
      allowedActionFn: (actionName: string) => boolean,
      refreshContentFn: () => void,
      dialogHelper: IDetailsDialogInAngularPlaceHelper,
  ) {

    // $scope.refreshContent is used by some of the menu actions too; be very careful about deleting it
    $scope.refreshContent = refreshContentFn;
    $scope.allowedAction = allowedActionFn;

    $scope.del = ($event: JQueryEventObject) => {
      this.DeleteDatasourcesAction.confirmDeleteDatasource($scope.datasource, ActionContext.fromEvent($event))
      .then((result: IDeleteDatasourcesActionResult) => {
        if (result.allowed) {
          this.$state.go('main.site.content.project', { id: $scope.datasource.project.id });
        }
      });
    };

    $scope.changeDescription = (newDescription: string) => {
      return this.ChangeDatasourceDescriptionAction.execute($scope.datasource, newDescription)
        .then(dialogHelper.inlineEditResultHandler);
    };

    $scope.changeOwner = ($event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.changeOwnerOnDatasources($scope.datasourceSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showRevisionHistory = () => {
      return this.RevisionHistoryAction.showDataSourceRevisionHistory($scope.datasource)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.moveToProject = ($event: JQueryEventObject) => {
      const actionContext = ActionContext.fromEvent($event);
      return this.ContentActions.moveDatasourcesToProject($scope.datasourceSet, actionContext)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    // Allow direct assignment of a remote query agent to a datasource
    $scope.showRemoteQueryAgent = FeatureFlags.bridgeLiveDirectAssignEnabled && !$scope.datasource.hasExtracts;

    $scope.currentRemoteQueryAgentDisplayName = () => {
      return RemoteQueryAgentHelper.getRemoteQueryAgentDisplayName($scope.datasource);
    };

    $scope.changeRemoteQueryAgent = () => {
      return this.ContentActions.changeRemoteQueryAgentOnDatasource($scope.datasource)
        .then(dialogHelper.actionFromSeparateDialogResultHandler);
    };

    $scope.showDatasourceCertification = FeatureFlags.datasourceCertificationEnabled;

    $scope.certificationStatusUpdated = () => {
      const fakeResult: null = null; // The handler needs a result, but it ignores it if not present
      dialogHelper.inlineEditResultHandler(fakeResult);
    };

    $scope.changeTags = (newTagsList: string[]) => {
      return this.ContentActions.setTagsOnDatasource($scope.datasource, newTagsList)
        .then(ResultHelper.rejectResultOnError($q))
        .then(() => {
          refreshContentFn();
          dialogHelper.dismissDialog();
        });
    };

    $scope.onNlpSettingSaved = () => {
      refreshContentFn();
      dialogHelper.dismissDialog();
    };
  }

  private constructStylishDatasourceCtrl(
    angularContext: AngularContext,
    $scope: IDataSourcePlaceScope,
    datasourceDetailWithUsage: IDataSourceDetailInfoWithUsage,
    $sanitize: ng.sanitize.ISanitizeService,
    TelemetryService: Telemetry,
    browserTitleService: BrowserTitleService,
    $location: ng.ILocationService,
    datasourceActions: IDatasourceActions,
    detailsDialogHelper: IDetailsDialogInAngularPlaceHelper,
    dataCatalogApiRequestsService: DataCatalogApiRequestsService
  ) {

    $scope.datasourceSpaceProps = {
      angularContext: angularContext,
      datasourceDetail: datasourceDetailWithUsage,
      datasourceActions: datasourceActions,
      openDetail: () => detailsDialogHelper.openDialog(datasourceDetailWithUsage),
      addToFavorites: () => { appStore.dispatch(addContentFavorite(datasourceDetailWithUsage.idForFavoriting, 'datasource')); },
      removeFromFavorites: () => { appStore.dispatch(removeContentFavorite(datasourceDetailWithUsage.id, datasourceDetailWithUsage.idForFavoriting, 'datasource', this.datasourceDetailInfo.name)); },
      sanitizer: $sanitize,
      telemetry: TelemetryService,
      showNLPTab: datasourceActions.showNlp && SessionSiteSelectors.isNLPServiceEnabled(appStore.getState()),
      bridgeEnabled: this.ServerService.isBridgeEnabled(),
      siteUrlName: SessionSiteSelectors.siteUrlName(appStore.getState()),
      dataCatalogApiRequestsService: dataCatalogApiRequestsService
    };

    browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: datasourceDetailWithUsage.name });

    // add url watchers except for filters
    const queryString = getQueryStringServiceFromAngular($location);
    this.currentPath = $location.path();
    const getPath = () => {
      return this.currentPath;
    };

    addUrlWatchersToScopeForExplorer({ $scope, $location, queryString, store: appStore, getPreviousPath: getPath }, {contentType: false, filters: false, sort: true});

    // Like the Explorer URL bindings, this listener dispatches an action to Redux to update the
    // current subplace any time the URL path changes within the datasource place. This is instead
    // of the old subplaces that were implemented using ui-view.
    $scope.$on('$stateChangeSuccess', () => this.changeSubspace());
  }

  private changeSubspace = async () => {
    const pathChanged = this.currentPath !== this.$location.path();
    this.currentPath = this.$location.path();

    let subspaceName = DatasourceSpaceConfigHelper.subspaceNameFromCurrentState(this.$state.current);
    const inPublishResultState = SpaceConfigHelper.isCurrentStatePublishResult(this.$state.current);

    this.initScopeForBreadcrumbs(this.$scope);

    if (!this.$scope.isInOwnerPanel && inPublishResultState) {
      // Publish result dialog displays the default subspace in background
      subspaceName = 'connections';
      if (this.$scope.datasourceSpaceProps.showNLPTab) {
        subspaceName = 'nlp';
      }
    }

    if (subspaceName === 'nlp' && !this.$scope.datasourceSpaceProps.showNLPTab) {
      // redirect to connections if shouldn't show nlp- this will call enterSpace with the correct
      // url and parameters
      this.$state.go('main.site.content.datasource.connections', null, { location: 'replace'});
      return;
    }

    appStore.dispatch(enterSpace(
      {
        item: this.$scope.datasourceSpaceProps.datasourceDetail,
        spaceType: DatasourceDefinition.contentType,
        subspaceName: subspaceName
      }
    ));

    clearAndSetSortOrderFromURL(appStore, this.$location);

    // If we just switched tabs we know content counts are up to date.
    // They will get set to false by the above EnterSpaceAction.
    if (pathChanged) {
      appStore.dispatch(contentCountsUpToDate());
    }

    appStore.dispatch(requestSpaceContents());

    if (inPublishResultState) {
      const result: IGetPreferredRefreshOptionResult = await this.DataSources.getPreferredRefreshOption(this.datasourceDetailInfo.id);
      this.datasourcePublishResultService.handlePublishedDatasource(this.datasourceDetailInfo, result);
    }
  }

  private initScopeForBreadcrumbs($scope: IDataSourcePlaceScope) {
    const subspaceName = DatasourceSpaceConfigHelper.subspaceNameFromCurrentState(this.$state.current);
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.projectHierarchy);
    const spacesSiteUrls = siteUrlsSelector(appStore.getState());

    $scope.isInOwnerPanel = subspaceName === 'askDataUsageAnalytics';
    if ($scope.isInOwnerPanel){
      appStore.dispatch(updateBreadcrumbs([
        {
          text: this.datasourceDetailInfo.name,
          href: this.$location.absUrl().replace('/askDataUsageAnalytics',''),
        }, {
        text: Messages.datasourceAskDataMetrics(),
      }]));
      return;
    }

    populateContentBreadcrumbs([...projectHierarchyWithType, this.datasourceDetailInfo], spacesSiteUrls);
  }
}

function redirectByExtractsAndRefreshMode($state: ng.ui.IStateService, datasource: IDataSourceDetailInfo): void {
  // Prevent deep links for live data sources
  if (!datasource.hasExtracts) {
    $state.go('^.connectedWorkbooks'); // Go to default sibling state
  }

  // Prevent deep links to the incorrect refresh schedules page
  RefreshPropertyHelper.redirectByDatasourceRefreshMode($state, datasource.refreshMode);
}

Module.instance().controller('DatasourcePlaceCtrl', DatasourcePlaceCtrl);
